<div class="container-fluid vh-100 login-page-container">
  <div class="row h-100">

    <!-- Left Side - Image Column -->
    <div class="col-md-6 d-none d-md-flex align-items-center justify-content-center bg-light p-0 overflow-hidden">
      <div class="login-vector-background"></div> 
      <img src="assets/images/loginImage.png" 
           alt="Application Visual" 
           class="img-fluid" 
           style="object-fit: contain">
      <div class="lms-logo-box">
          <h1 class="lms-logo-text">LMS</h1>
      </div>
    </div>

    <!-- Right Side - Login Form Column -->
    <div class="col-md-5 flex-column justify-content-center login-right-column">

      <!-- Dynamic Heading & Subheading -->
      <h1 class="fw-bold login-heading mb-0">{{ currentHeading }}</h1>
      <p class="text-muted mt-1 login-subheading">{{ currentSubheading }}</p>

      <!-- Back Button (for non-login modes) -->
      <button *ngIf="showBackButton" type="button" class="btn btn-link mb-3" (click)="goBackToLogin()">
        ← Back to Login
      </button>

      <!-- Move script outside form to avoid HTML parsing issues -->
      <script src="https://accounts.google.com/gsi/client" async defer></script>

      <!-- Dynamic Forms based on mode -->
      <form *ngIf="mode === 'login'" [formGroup]="loginForm" (ngSubmit)="loginWithEmail()">
        <div class="email-input mb-4">
          <label for="email" class="email-label">Email*</label>
          <input id="email" type="email" formControlName="email" class="form-control custom-input" required>
          <div class="invalid-feedback" *ngIf="loginForm.get('email')?.hasError('required') && loginForm.get('email')?.touched">
            Email is required.
          </div>
          <div class="invalid-feedback" *ngIf="loginForm.get('email')?.hasError('email') && loginForm.get('email')?.touched">
            Please enter a valid email address.
          </div>
        </div>
        <div class="password-input mb-4">
          <label for="password" class="password-label">Password*</label>
          <input id="password" type="password" formControlName="password" class="form-control custom-input" required>
          <div class="invalid-feedback" *ngIf="loginForm.get('password')?.hasError('required') && loginForm.get('password')?.touched">
            Password is required.
          </div>
          <div class="invalid-feedback" *ngIf="loginForm.get('password')?.hasError('minlength') && loginForm.get('password')?.touched">
            Password must be at least 8 characters long.
          </div>
          <a class="forgot-password-link" (click)="onForgotPassword()">Forgot password?</a>
        </div>
        <button type="submit" class="btn btn-primary w-100" [disabled]="loginForm.invalid || isLoading">
          {{ isLoading ? 'Logging in...' : 'Login' }}
        </button>
      </form>

      <form *ngIf="mode === 'forgot-email'" [formGroup]="forgotEmailForm" (ngSubmit)="sendResetEmail()">
        <div class="email-input mb-4">
          <label for="email" class="email-label">Email*</label>
          <input id="email" type="email" formControlName="email" class="form-control custom-input" required>
          <div class="invalid-feedback" *ngIf="forgotEmailForm.get('email')?.hasError('required') && forgotEmailForm.get('email')?.touched">
            Email is required.
          </div>
          <div class="invalid-feedback" *ngIf="forgotEmailForm.get('email')?.hasError('email') && forgotEmailForm.get('email')?.touched">
            Please enter a valid email address.
          </div>
        </div>
        <button type="submit" class="btn btn-primary w-100" [disabled]="forgotEmailForm.invalid || isLoading">
          {{ isLoading ? 'Sending...' : 'Send Reset Link' }}
        </button>
      </form>
      <form *ngIf="mode === 'reset-password'" [formGroup]="resetForm" (ngSubmit)="setNewPassword()">
        <!-- Hidden token field - auto-filled by TS -->
        <input type="hidden" formControlName="resetToken">
        
        <div class="password-input mb-4">
          <label for="newPassword" class="password-label">New Password*</label>
          <input id="newPassword" type="password" formControlName="newPassword" class="form-control custom-input" required>
          <div class="invalid-feedback" *ngIf="resetForm.get('newPassword')?.hasError('required') && resetForm.get('newPassword')?.touched">
            New password is required.
          </div>
          <div class="invalid-feedback" *ngIf="resetForm.get('newPassword')?.hasError('minlength') && resetForm.get('newPassword')?.touched">
            New password must be at least 8 characters long.
          </div>
        </div>
        <div class="password-input mb-4">
          <label for="confirmPassword" class="password-label">Confirm New Password*</label>
          <input id="confirmPassword" type="password" formControlName="confirmPassword" class="form-control custom-input" required>
          <div class="invalid-feedback" *ngIf="resetForm.get('confirmPassword')?.hasError('required') && resetForm.get('confirmPassword')?.touched">
            Confirmation is required.
          </div>
          <div class="invalid-feedback" *ngIf="resetForm.get('confirmPassword')?.hasError('minlength') && resetForm.get('confirmPassword')?.touched">
            Confirmation must be at least 8 characters long.
          </div>
          <div class="invalid-feedback" *ngIf="resetForm.hasError('mismatch') && (resetForm.get('confirmPassword')?.touched || resetForm.get('newPassword')?.touched)">
            Passwords do not match.
          </div>
        </div>
        <button type="submit" class="btn btn-primary w-100" [disabled]="resetForm.invalid || isLoading">
          {{ isLoading ? 'Setting...' : 'Set New Password' }}
        </button>
      </form>

      <!-- Error message display -->
      <div *ngIf="errorMessage" class="alert alert-danger mt-3">
        {{ errorMessage }}
        <small *ngIf="errorDetails" class="d-block mt-1">Details: {{ errorDetails }}</small>
      </div>
      <!-- Success message display (if needed) -->
      <div *ngIf="successMessage" class="alert alert-success mt-3">{{ successMessage }}</div>

      <ng-container *ngIf="currentUser$ | async as user; else loggedOutContent">
        <!-- Divider for logged-in state -->
        <div class="divider-with-text">
          <div class="line"></div>
          <span class="text">or</span>
          <div class="line"></div>
        </div>
        <!-- "Login to different account" Row -->
        <div class="d-flex justify-content-between align-items-center mb-4">
          <span class="small login-different-account-text">Login to a different account</span>
          <button mat-flat-button
                  class="login-diff-button"
                  aria-label="Login with different account"
                  (click)="loginWithGoogle()"
                  [disabled]="isLoading"
                  title="Login with a different Google account">
            {{ isLoading ? 'Logging in...' : 'Login' }}
          </button>
        </div>
      </ng-container>

      <!-- Logged Out View Template (only show in login mode) -->
      <ng-template #loggedOutContent>
        <div *ngIf="showGoogleSignIn">
          <!-- Divider for logged-out state -->
          <div class="divider-with-text">
            <div class="line"></div>
            <span class="text">or</span>
            <div class="line"></div>
          </div>
          <div class="d-grid gap-2 mb-4">
            <button matButton="outlined"
                  (click)="loginWithGoogle()"
                  [disabled]="isLoading"
                  class="google-signin-button"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 18" class="google-g-logo me-2">
              
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-1 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span class="google-sign-in-words">{{ isLoading ? 'Logging in to...' : 'Sign in with ' }} <strong>Google</strong></span>
            </button>
          </div>
        </div>
      </ng-template>
    </div> <!-- End Right Column -->
  </div> <!-- End Row -->
</div> <!-- End Container -->